import { X } from "lucide-react"
import { AnimatePresence, motion, useReducedMotion } from "motion/react"
import { type ReactNode, useEffect, useId, useRef } from "react"
import { createPortal } from "react-dom"

/**
 * Ficha lateral: entra por la derecha y se va por donde vino.
 *
 * ⚠ **No es un modal, y la diferencia importa.** Un modal tapa el resto porque hay
 * que atenderlo antes de seguir; una ficha acompaña a lo que se está mirando. Por eso
 * **no** atrapa el foco ni bloquea el fondo: se puede seguir usando el tablero con
 * la ficha abierta, que es justo para lo que sirve —comparar una tarea con las
 * demás—. Escape la cierra igualmente, porque es lo que todo el mundo prueba.
 *
 * ⚠ **Se pinta en `document.body` con un portal, no donde se declara.** El área de
 * trabajo es cristal con `backdrop-filter`, y en CSS eso convierte a ese elemento en
 * el bloque contenedor de todo lo `fixed` que lleve dentro: el cajón quedaba atrapado
 * en el área —más baja que la ventana y con `overflow: hidden`—, así que su `h-dvh`
 * le sobraba por abajo y el final de la ficha salía recortado, sin forma de
 * desplazarse hasta él. Fuera del área, `fixed` vuelve a medirse contra la ventana.
 *
 * ⚠ **Relleno de sobra abajo.** El botón de reportar flota en la esquina inferior
 * derecha, justo encima del final de la ficha; sin ese hueco taparía el último bloque.
 *
 * ⚠ **`w-full` con un tope, no un ancho fijo.** En una pantalla estrecha ocupa el
 * ancho entero —una ficha de 560 px en un portátil de 1280 no deja tablero que
 * mirar— y en una de escritorio se detiene antes de comerse la mitad de la vista,
 * que es de lo que la ficha quiere que se despegue la atención lo menos posible.
 *
 * ⚠ **Entra desplazándose, no apareciendo.** El desplazamiento dice de dónde salió y
 * hacia dónde volverá; una ficha que aparece de la nada obliga a buscar de qué
 * elemento es. Se anima `transform`, nunca la maquetación.
 */

/**
 * Cuánto ocupa la ficha en una pantalla ancha.
 *
 * ⚠ **`amplio` no es «más grande porque sí»: es para las fichas que TRABAJAN.** La de
 * una orden lleva una lista de conceptos con cantidad y precio, un desglose fiscal y
 * un formulario para añadir: en 560 px cada línea se parte en dos y el bloque entero
 * se lee como una pared. Las de sólo lectura se quedan estrechas a propósito, porque
 * lo que hay detrás —el tablero— sigue siendo lo que se está mirando.
 */
const ANCHOS = {
  normal: "max-w-[560px]",
  amplio: "max-w-[860px]",
} as const

type Props = {
  abierto: boolean
  titulo: string
  onCerrar: () => void
  children: ReactNode
  ancho?: keyof typeof ANCHOS
}

export function Cajon({ abierto, titulo, onCerrar, children, ancho = "normal" }: Props) {
  const sinMovimiento = useReducedMotion()
  const idTitulo = useId()

  /**
   * ⚠ Igual que en `Modal`: `onCerrar` en una referencia y fuera de las
   * dependencias. Aquí el efecto no toca el foco, así que no llegaba a robarlo —
   * pero desmontaba y volvía a montar el escuchador de teclado en CADA tecla que se
   * escribiera dentro de la ficha, y el día que este efecto haga algo más que
   * escuchar, sería el mismo fallo que allí. La referencia se actualiza en cada
   * render para que Escape llame siempre a la función vigente.
   */
  const alCerrar = useRef(onCerrar)
  useEffect(() => {
    alCerrar.current = onCerrar
  })

  useEffect(() => {
    if (!abierto) return
    const alTeclear = (e: KeyboardEvent) => {
      if (e.key === "Escape") alCerrar.current()
    }
    document.addEventListener("keydown", alTeclear)
    return () => document.removeEventListener("keydown", alTeclear)
  }, [abierto])

  return createPortal(
    <AnimatePresence>
      {abierto && (
        <motion.aside
          // `complementary` y no `dialog`: acompaña al contenido, no lo interrumpe.
          aria-labelledby={idTitulo}
          // `no-print`: una ficha lateral nunca es lo que se imprime. Lo que se imprime
          // desde ella —el carnet— sale por su pliego, que va fuera del cajón.
          // ⚠ **El cajón NO se desplaza: se desplaza su cuerpo.** La cabecera —título y
          // cerrar— se queda arriba aunque la ficha sea larga, y respeta la barra de
          // estado del iPhone (`env(safe-area-inset-top)`): pegada a `top-0`, la X caía
          // debajo de la hora y la batería, y la ficha no había forma de cerrarla.
          className={`no-print fixed top-0 right-0 z-40 flex h-dvh w-full ${ANCHOS[ancho]} flex-col border-l`}
          style={{
            backgroundColor: "var(--surface)",
            borderColor: "var(--border)",
            boxShadow: "var(--elev-3)",
          }}
          initial={sinMovimiento ? false : { x: "100%" }}
          animate={{ x: 0 }}
          exit={sinMovimiento ? { opacity: 0 } : { x: "100%" }}
          transition={
            sinMovimiento
              ? { duration: 0 }
              : { type: "spring", stiffness: 420, damping: 38, mass: 0.8 }
          }
        >
          <div
            className="flex shrink-0 items-center justify-between gap-3 border-b px-4 pt-[max(0.5rem,env(safe-area-inset-top))] pb-2"
            style={{ borderColor: "var(--border)" }}
          >
            <h2 id={idTitulo} className="min-w-0 text-sm font-medium uppercase tracking-wide">
              {titulo}
            </h2>
            <button
              type="button"
              onClick={onCerrar}
              aria-label="Cerrar la ficha"
              // 44 px: se pulsa con el pulgar, y es la única salida de la ficha.
              className="boton-secundario flex h-11 w-11 shrink-0 items-center justify-center p-0"
            >
              <X size={18} aria-hidden={true} />
            </button>
          </div>
          {/* El hueco de abajo es el del botón de reportar, que flota en esa esquina,
              más la barra de gestos del iPhone. */}
          <div className="barra-area min-h-0 flex-1 overflow-y-auto px-4 pt-3 pb-[calc(6rem+env(safe-area-inset-bottom))]">
            {children}
          </div>
        </motion.aside>
      )}
    </AnimatePresence>,
    document.body,
  )
}
